// ─── What a day looks like, decided once ─────────────────────────────────────
//
// The four calendar states are the product's visual language, and the mapping
// from real data to state must live in ONE place. Two views deciding
// separately what "full" means is how a day reads as available in the month
// grid and full in the week grid — the calendar contradicting itself, which is
// worse than either answer alone.
//
// Deliberately pure and synchronous: given counts, it returns a state. No
// queries, no dates, no timezone. That makes it exhaustively testable, and it
// means the month view, the week view, the mobile view and the customer-facing
// page cannot drift apart.

export const DAY_STATES = ["blocked", "full", "open", "selected"] as const;
export type DayState = (typeof DAY_STATES)[number];

/** Which side of the product is reading a label. */
export type Surface = "business" | "customer";

export type DayFacts = {
  /** Is the resource working at all that day? False for a closed day. */
  working: boolean;
  /** Slots a customer could actually take. */
  openSlots: number;
  /** Bookings that exist, whatever their status. Drives the volume dots on
   *  the business side. */
  bookings: number;
  /** Is this the day the user has focused? */
  selected?: boolean;
};

/**
 * The state a day should render in.
 *
 * Order matters and is not arbitrary:
 *
 *   selected wins over everything — it marks where the user is, and a
 *   focused day that renders as "full" leaves them unable to see what they
 *   selected.
 *
 *   blocked beats full — a closed day and a fully-booked day are different
 *   facts, and "closed" is the more useful one to show. A day with no working
 *   hours is not "fully booked"; nobody could ever have booked it.
 *
 *   full beats open — a day with no remaining capacity reads as full even if
 *   the arithmetic left a stray slot the service cannot fit into.
 */
export function dayState(facts: DayFacts): DayState {
  if (facts.selected) return "selected";
  if (!facts.working) return "blocked";
  return facts.openSlots > 0 ? "open" : "full";
}

/**
 * The words that go with the colour.
 *
 * "Not colour-only — support labels/counts/patterns/status text too." This is
 * that support, and it lives beside the state so a component cannot render one
 * without the other being available.
 *
 * ── Why this no longer says "31 available" ──────────────────────────────────
 *
 * It used to show the open-slot count, and Drea's decision on 2026-09-12 was
 * to take open-slot counts off the month view entirely. The reasoning is worth
 * keeping because it is about what a number MEANS rather than about taste:
 * slots come off a fifteen-minute grid, so an ordinary empty weekday read "31
 * available" — while her real day holds three or four appointments. The number
 * was arithmetically true and told a salon owner nothing, on every tile of
 * every month.
 *
 * What replaces it differs by surface, which is why the surface is a parameter
 * now rather than a detail of the caller:
 *
 *   BUSINESS  how many bookings — the fact she is actually scanning for.
 *             Three or four is a real number about a real day.
 *   CUSTOMER  whether anything is free, and nothing more. A booking count
 *             would leak how busy the business is, and a free-slot count is
 *             the same meaningless grid arithmetic.
 */
export function dayStateLabel(
  state: DayState, facts: DayFacts, surface: Surface = "business",
): string {
  if (state === "blocked") return "Closed";
  if (state === "selected" && !facts.working) return "Closed";

  const full = state === "full" || (state === "selected" && facts.openSlots <= 0);

  if (surface === "customer") {
    // No number on either branch. "Available" is the whole message a customer
    // needs from a month tile; the times themselves are the next screen.
    return full ? "Fully booked" : "Available";
  }

  // The count is what she is scanning for, but a FULL day has to keep saying
  // so — the colour carries that, and the label is the non-colour half of the
  // same signal. Dropping "full" here would leave anyone who cannot separate
  // the hues unable to tell a busy day from a closed one.
  const count = facts.bookings === 1 ? "1 booking" : `${facts.bookings} bookings`;
  if (facts.bookings > 0) return full ? `${count} · full` : count;

  // ── An empty working day says AVAILABLE, not "nothing booked" ────────────
  //
  // Drea, 2026-09-15. "Nothing booked" is the same fact stated as an absence,
  // and a month of them reads as a month of nothing happening rather than a
  // month with room in it. What she is looking at when she scans the grid is
  // where she could put somebody.
  //
  // The same word the customer surface uses, deliberately: the product should
  // have ONE word for "there is room here", and two would eventually drift
  // into meaning two different things.
  //
  // "Free all day" was the other candidate and is not used, because it would
  // overclaim on a half-day — a Wednesday she works 09:00 to 13:00 with
  // nothing in it is available, and is not free all day.
  //
  // FULL is unchanged, and stays negative on purpose. A day with no bookings
  // that is still full is blocked out rather than taken — a holiday, or a
  // morning given to something else — and the label is the non-colour half of
  // that signal.
  return full ? "Fully booked" : "Available";
}

/**
 * How many volume dots to draw, business-side.
 *
 * Capped, because the dots communicate "roughly how busy" and a day with
 * fourteen appointments does not need fourteen dots — it needs to read as
 * busy at a glance. The real count is in the label for anyone who needs it.
 *
 * CUSTOMER-SIDE THIS MUST NOT BE USED. Showing a customer how busy the
 * business is leaks commercial information; the customer view draws
 * availability instead. Flagged on issue #4 — the design doc asks for
 * "booking-volume dots generated from real bookings" without distinguishing
 * the two surfaces.
 */
export const MAX_VOLUME_DOTS = 4;

export function volumeDots(bookings: number): number {
  return Math.max(0, Math.min(MAX_VOLUME_DOTS, bookings));
}

/** The customer-facing equivalent: how much is free, not how much is taken. */
export function availabilityDots(openSlots: number): number {
  if (openSlots <= 0) return 0;
  if (openSlots <= 2) return 1;
  if (openSlots <= 5) return 2;
  return 3;
}
